import { TFunction, useTranslation } from "react-i18next"
import { InputModel } from "../../../../utils/types"
import { GridColDef } from "@mui/x-data-grid"
import StatusComponent from "../../../../components/status"
import { getStatus } from "../../../../hooks/status"
import { Color } from "../../../../utils/theme/types/color"
import { Status } from "../../../../utils/enums"
import { Box, Typography } from "@mui/material"
import { intervaleList } from "../../../../utils/constants/intervaleList"

const useModelInput = () => {
    const { t } = useTranslation()
    return {
        intervale: {
            label: 'general.tags.intervale',
            translate: true,
            name: 'intervale',
            type: 'select-search',
            items: intervaleList,
            value: null,
            wrapChildren: ({ children, id }) => {
                return (
                    <Box key={id}>
                        <Typography sx={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#707070',
                            marginBottom: '20px',
                        }}>{t('general.intervale.intervale')}</Typography>

                        {children}
                    </Box>
                )
            }
        },
        from: {
            label: 'general.tags.from',
            translate: true,
            name: 'from',
            type: 'date',
            value: null,
            rest: {
                max: 'to',
            },
            size: {
                xl: 6,
                lg: 6,
                md: 6,
                sm: 6,
                xs: 6,
            },
            wrapChildren: ({ children, id }) => {
                return (
                    <Box key={id}>
                        <Typography sx={{
                            fontSize: '16px',
                            fontWeight: '600',
                            color: '#707070',
                            marginBottom: '20px',
                        }}>{t('general.filters')}</Typography>

                        {children}
                    </Box>
                )
            }
        },
        to: {
            label: 'general.tags.to',
            translate: true,
            name: 'to',
            type: 'date',
            value: null,
            rest: {
                min: 'from',
            },
            size: {
                xl: 6,
                lg: 6,
                md: 6,
                sm: 6,
                xs: 6,
            },
            itemChildren: {
                alignSelf: "end"
            }
        },
        area: {
            label: 'general.tags.area',
            translate: true,
            name: 'area',
            type: 'filter-select',
            items: [],
            value: null,
        },
        department: {
            label: 'general.tags.department',
            translate: true,
            name: 'department',
            type: 'filter-select',
            items: [],
            value: [],
        },
        questionnaire: {
            label: 'general.tags.questionnaire',
            translate: true,
            name: 'questionnaire',
            type: 'filter-select',
            items: [],
            value: [],
        },
        percentage: {
            label: 'Porcentaje de avance',
            name: 'percentage',
            type: 'percentage',
            value: null,
        },
        division: {
            label: 'general.tags.division',
            translate: true,
            name: 'division',
            type: 'filter-select',
            items: [],
            value: null,
            size: {
                xl: 12,
                lg: 12,
                md: 12,
                sm: 12,
            },
        },
        unidad_id: {
            label: 'general.tags.unit',
            translate: true,
            name: 'unidad_id',
            type: 'filter-select',
            items: [],
            value: null,
            size: {
                xl: 12,
                lg: 12,
                md: 12,
                sm: 12,
            },
        },
        user: {
            label: 'general.tags.user',
            translate: true,
            name: 'user',
            type: 'filter-select',
            items: [],
            value: null,
        },
    } as { [x: string]: InputModel }
}

const columsTranslate = ({ t, colors, intervale }: { t: TFunction<"translation", undefined>; colors: Color; intervale: string  }): GridColDef[] => {
    let column = {
        '1d': ([
            {
                field: 'unidad_ceco',
                headerName: t('general.tags.id'),
                width: 100,
            },
            {
                field: 'unidad',
                headerName: t('general.tags.store'),
                width: 200,
            },
            {
                field: 'area',
                headerName: t('general.tags.area'),
                width: 150,
            },
            {
                field: 'departamento',
                headerName: t('general.tags.department'),
                width: 200,
            },
            {
                field: 'usuario',
                headerName: 'Usuario asignado',
                width: 200,
            },
            {
                field: 'fecha_inicio',
                headerName: 'Fecha de inicio del cuestionario',
                width: 240,
            },
            {
                field: 'cuestionario',
                headerName: 'Nombre del cuestionario',
                width: 200,
            },
            {
                field: 'avance',
                headerName: 'Porcentaje de avance',
                width: 170,
            },
            {
                field: 'division_unidad',
                headerName: 'División de la unidad',
                width: 200,
            },
            {
                field: 'division_usuario',
                headerName: 'División de usuario',
                width: 200,
            },
            {
                field: 'cuestionario_estatus',
                headerName: 'Estado del cuestionario',
                width: 200,
                renderCell: (row: any) => {
                    const statusQuestionnaire = getStatus(row.row.cuestionario_estatus_id as Status, colors, t)
                    return (
                        <StatusComponent
                            color={statusQuestionnaire?.color}
                            msg={statusQuestionnaire?.text}
                            size="small"
                        />
                    )
                },
            },
        ]),
        '1w': ([
            {
                field: 'unidad_ceco',
                headerName: t('general.tags.id'),
                width: 100,
            },
            {
                field: 'unidad',
                headerName: t('general.tags.store'),
                width: 200,
            },
            {
                field: 'area',
                headerName: t('general.tags.area'),
                width: 150,
            },
            {
                field: 'departamento',
                headerName: t('general.tags.department'),
                width: 200,
            },
            {
                field: 'usuario',
                headerName: 'Usuario asignado',
                width: 200,
            },
            {
                field: 'semana',
                headerName: t('general.week'),
                width: 150,
            },
            {
                field: 'cuestionario',
                headerName: 'Nombre del cuestionario',
                width: 200,
            },
            {
                field: 'avance',
                headerName: 'Promedio de avance',
                width: 170,
            },
            {
                field: 'division_unidad',
                headerName: 'División de la Unidad',
                width: 200,
            },
            {
                field: 'division_usuario',
                headerName: 'División de Usuario',
                width: 200,
            },
            {
                field: 'cuestionario_estatus',
                headerName: 'Estado del cuestionario',
                width: 200,
                renderCell: (row: any) => {
                    const statusQuestionnaire = getStatus(row.row.cuestionario_estatus_id as Status, colors, t)
                    return (
                        <StatusComponent
                            color={statusQuestionnaire?.color}
                            msg={statusQuestionnaire?.text}
                            size="small"
                        />
                    )
                },
            },
        ]),
    }
    return intervale == '1w' ? column["1w"] : column["1d"]
}

const statusList = [Status.WAITING, Status.TO_BE_STARTED, Status.FINISHED, Status.INCOMPLETE, Status.EXPIRED, Status.CANCEL]
export { useModelInput, statusList, columsTranslate }